import type { MarketplaceService, VariantPricing } from '../../types/pricing';

export interface QuantityAddonMetadata {
  config_key?: string;
  unit_label?: string;
  default_quantity?: number;
  max_quantity?: number;
  included_quantity?: number;
  price_cents?: number;
  frequency?: string;
  type?: string;
}

export interface AdditionalUserOffer {
  enabled: boolean;
  includedUsers: number;
  pricePerUserCents: number;
  unitLabel: string;
  maxQuantity: number | null;
  frequency: string;
  variant?: VariantPricing;
}

export interface ServiceConfigurationPayload {
  additional_users?: number;
}

function getPrimaryQuantityAddon(service: MarketplaceService): QuantityAddonMetadata | null {
  const quantityAddons = Array.isArray(service.metadata?.quantity_addons)
    ? service.metadata.quantity_addons
    : [];
  return quantityAddons[0] || null;
}

export function getSelectedVariant(service: MarketplaceService, variantKey?: string): VariantPricing | undefined {
  return service.variants?.variants?.find((variant) => variant.key === variantKey);
}

export function getIncludedUsersFromDescription(description?: string | null): number {
  if (!description) return 0;

  const overMatch = description.match(/over\s+(\d+)/i);
  if (overMatch) return Number(overMatch[1]) || 0;

  const upToMatch = description.match(/up to\s+(\d+)\s+users?/i);
  if (upToMatch) return Number(upToMatch[1]) || 0;

  const plusUsersMatch = description.match(/(\d+)\+\s+users?/i);
  if (plusUsersMatch) return Number(plusUsersMatch[1]) || 0;

  return 0;
}

export function getAdditionalUserOffer(
  service: MarketplaceService,
  variantKey?: string,
): AdditionalUserOffer {
  const quantityAddon = getPrimaryQuantityAddon(service);
  const variant = getSelectedVariant(service, variantKey);
  const includedUsers = Math.max(
    0,
    Number(quantityAddon?.included_quantity ?? getIncludedUsersFromDescription(variant?.description) ?? 0) || 0,
  );
  const pricePerUserCents = Math.max(
    0,
    Number(quantityAddon?.price_cents ?? service.metadata?.additional_user_price_cents ?? 0) || 0,
  );
  const unitLabel = String(quantityAddon?.unit_label || 'user').trim() || 'user';
  const maxQuantity = Number.isFinite(Number(quantityAddon?.max_quantity))
    ? Number(quantityAddon?.max_quantity)
    : null;
  const frequency = String(quantityAddon?.frequency || variant?.pricing?.frequency || 'monthly');

  return {
    enabled: Boolean(variant && pricePerUserCents > 0),
    includedUsers,
    pricePerUserCents,
    unitLabel,
    maxQuantity,
    frequency,
    variant,
  };
}

export function clampAdditionalUsers(value: number, maxQuantity?: number | null): number {
  if (!Number.isFinite(value)) return 0;
  const normalized = Math.max(0, Math.floor(value));
  if (Number.isFinite(Number(maxQuantity)) && Number(maxQuantity) >= 0) {
    return Math.min(normalized, Number(maxQuantity));
  }
  return normalized;
}

export function getAdditionalUserTotalCents(
  service: MarketplaceService,
  variantKey?: string,
  additionalUsers: number = 0,
): number {
  const offer = getAdditionalUserOffer(service, variantKey);
  return offer.enabled ? clampAdditionalUsers(additionalUsers, offer.maxQuantity) * offer.pricePerUserCents : 0;
}
